import { guestPlayPath } from '@awwyeah/shared';

/**
 * Where the freshly-minted play link lives between "Invite by link" and the board (WS-23).
 *
 * The server hands back the raw token exactly once — that is the whole point of storing only
 * its hash — so if the creator is going to see a copyable URL on the board they just
 * navigated to, something has to carry it there. `sessionStorage`, keyed by game, is the
 * narrowest thing that works: it dies with the tab, it never reaches another device, and
 * losing it is harmless (the panel then offers a fresh link instead of a copyable one, which
 * is the honest answer anyway — a link you can no longer see is a link you must regenerate).
 *
 * Deliberately *not* `localStorage`: a token that outlives the tab is a token sitting on disk
 * for no benefit.
 */

const KEY_PREFIX = 'awwyeah.guestLink.';

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Private-mode Safari and friends throw on access rather than returning null.
    return null;
  }
}

export function stashGuestToken(gameId: string, token: string): void {
  try {
    storage()?.setItem(`${KEY_PREFIX}${gameId}`, token);
  } catch {
    // A full or disabled store just means the panel offers "new link" instead of a URL.
  }
}

export function readGuestToken(gameId: string): string | null {
  try {
    return storage()?.getItem(`${KEY_PREFIX}${gameId}`) ?? null;
  } catch {
    return null;
  }
}

export function clearGuestToken(gameId: string): void {
  try {
    storage()?.removeItem(`${KEY_PREFIX}${gameId}`);
  } catch {
    // Nothing to do — a stale entry is superseded by the next mint anyway.
  }
}

/** The absolute URL to send. Pure, so a test can assert it without a browser. */
export function buildGuestPlayUrl(origin: string, token: string): string {
  return `${origin.replace(/\/$/, '')}${guestPlayPath(token)}`;
}
